import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ActionButtons from '../components/ActionButtons';
import { usePermissions } from '../context/PermissionsContext';

interface Report {
  id: number;
  name: string;
  description: string;
  query: string;
  category: string;
  is_row_report: boolean;
  is_active: boolean;
}

const CATEGORIES = [
  { value: 'General', label: 'Geral' },
  { value: 'Staff', label: 'Colaboradores' },
  { value: 'Parent', label: 'Pais/Responsáveis' },
  { value: 'Meeting', label: 'Reuniões' },
  { value: 'BibleStudy', label: 'Estudos Bíblicos' },
  { value: 'Event', label: 'Eventos' },
  { value: 'Incident', label: 'Ocorrências' },
  { value: 'Project', label: 'Projetos' },
  { value: 'Survey', label: 'Pesquisas' },
  { value: 'Content', label: 'Conteúdos' },
  { value: 'SchoolClass', label: 'Turmas' },
];

const ReportsPage: React.FC = () => {
  const { permissions } = usePermissions();
  const isSA = permissions === 'all';

  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState<Report | null>(null);

  // Data for the running report
  const [runningReport, setRunningReport] = useState<Report | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [executing, setExecuting] = useState(false);
  const [params, setParams] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'General',
    query: 'SELECT * FROM school_staff',
    is_row_report: false
  });

  const fetchReports = useCallback(async () => {
    try {
      const response = await api.get('/custom-reports/');
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const detectParams = (query: string) => {
    const regex = /%\((\w+)\)s/g;
    const found: string[] = [];
    let match;
    while ((match = regex.exec(query)) !== null) {
      if (!found.includes(match[1])) {
        found.push(match[1]);
      }
    }
    return found;
  };

  const handleCreateNew = () => {
    if (!isSA) return;
    setEditingReport(null);
    setFormData({
      name: '',
      description: '',
      category: 'General',
      query: 'SELECT * FROM school_staff',
      is_row_report: false
    });
    setResults([]);
    setRunningReport(null);
    setShowForm(true);
  };

  const handleEdit = (report: Report) => {
    if (!isSA) return;
    setEditingReport(report);
    setFormData({
      name: report.name,
      description: report.description,
      category: report.category,
      query: report.query,
      is_row_report: report.is_row_report
    });
    setResults([]);
    setRunningReport(null);
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSA) return;
    try {
      if (editingReport) {
        await api.patch(`/custom-reports/${editingReport.id}/`, formData);
      } else {
        await api.post('/custom-reports/', formData);
      }
      setShowForm(false);
      fetchReports();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao salvar relatório');
    }
  };

  const handleDelete = async (id: number) => {
    if (!isSA) return;
    if (!window.confirm('Excluir este relatório permanentemente?')) return;
    try {
      await api.delete(`/custom-reports/${id}/`);
      fetchReports();
    } catch (error) {
      console.error("Error deleting report", error);
    }
  };

  const handleRun = async (report: Report) => {
    setRunningReport(report);

    // Deixa os params se já existirem, ou reseta se for novo report
    if (!runningReport || runningReport.id !== report.id) {
      const detected = detectParams(report.query);
      const newParams: Record<string, string> = {};
      detected.forEach(p => newParams[p] = '');
      setParams(newParams);
    }

    setExecuting(true);
    setResults([]);
    try {
      // Usamos POST para enviar os parâmetros
      const response = await api.post(`/custom-reports/${report.id}/run/`, params);
      setResults(response.data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao executar consulta SQL');
    } finally {
      setExecuting(false);
    }
  };

  const escapeHtml = (unsafe: string) => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || results.length === 0) return;

    const headers = Object.keys(results[0]);
    
    const html = `
      <html>
        <head>
          <title>${escapeHtml(runningReport?.name || 'Relatório')}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { color: #333; margin-bottom: 5px; }
            p { margin: 5px 0; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(runningReport?.name || '')}</h1>
          <p>${escapeHtml(runningReport?.description || '')}</p>
          <p><small>Gerado em: ${new Date().toLocaleString()}</small></p>
          <table>
            <thead>
              <tr>
                ${headers.map(key => `<th>${escapeHtml(key.replace(/_/g, ' ').toUpperCase())}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${results.map(row => `
                <tr>
                  ${headers.map(key => {
                    const val = row[key];
                    return `<td>${val !== null ? escapeHtml(String(val)) : '-'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              // window.close(); // Opcional: fechar após imprimir
            };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportExcel = () => {
    if (results.length === 0) return;

    const headers = Object.keys(results[0]);
    const csvContent = [
      headers.join(';'),
      ...results.map(row => headers.map(header => {
        const value = row[header] !== null ? String(row[header]) : '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${runningReport?.name || 'relatorio'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div style={{ padding: '20px' }}>Carregando relatórios...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1>Relatórios Dinâmicos</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerador de listagens baseado em SQL com variáveis</p>
        </div>
        {!showForm && !runningReport && isSA && (
          <button className="btn" onClick={handleCreateNew}>+ Novo Relatório</button>
        )}
        {(showForm || runningReport) && (
          <button className="btn btn-secondary" onClick={() => { setShowForm(false); setRunningReport(null); }}>Voltar</button>
        )}
      </div>

      {showForm ? (
        <div className="card" style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h3>{editingReport ? 'Editar Relatório' : 'Novo Relatório'}</h3>
          <form onSubmit={handleSave}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label>Nome do Relatório</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Lista de Alunos por Turma"
                />
              </div>
              <div>
                <label>Categoria (Onde mostrar)</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_row_report}
                  onChange={e => setFormData({ ...formData, is_row_report: e.target.checked })}
                />
                <span>Relatório de Linha (Disponível em cada registro da tabela)</span>
              </label>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '25px' }}>
                Geralmente utilizado com a variável `%(id)s` para filtrar um registro específico.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label>Descrição (opcional)</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrição do que este relatório extrai"
                style={{ height: '60px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label>Consulta SQL (SELECT)</label>
              <textarea
                required
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '0.9rem', backgroundColor: '#1e1e1e', color: '#d4d4d4', padding: '15px' }}
                value={formData.query}
                onChange={e => setFormData({ ...formData, query: e.target.value })}
                placeholder="SELECT * FROM ..."
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Utilize `%(nome_da_variavel)s` para parâmetros dinâmicos.
                </p>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {detectParams(formData.query).map(p => (
                    <span key={p} style={{ fontSize: '0.75rem', backgroundColor: 'var(--accent-red)', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn">Salvar Relatório</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      ) : runningReport ? (
        <div className="card" style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: 0 }}>{runningReport.name}</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{runningReport.description}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-secondary" onClick={handleExportExcel} disabled={results.length === 0}>Exportar Excel</button>
              <button className="btn btn-secondary" onClick={handlePrint} disabled={results.length === 0}>Imprimir</button>
              <button className="btn" onClick={() => handleRun(runningReport)}>Gerar / Atualizar</button>
            </div>
          </div>

          {Object.keys(params).length > 0 && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Variáveis da Consulta:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                {Object.keys(params).map(p => (
                  <div key={p}>
                    <label style={{ fontSize: '0.8rem' }}>{p}</label>
                    <input
                      type="text"
                      value={params[p]}
                      onChange={e => setParams({ ...params, [p]: e.target.value })}
                      placeholder={`Valor para ${p}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {executing ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Executando consulta...</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-light)' }}>
              Nenhum dado retornado. Clique em Gerar para executar.
            </div>
          ) : (
            <table style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  {Object.keys(results[0]).map(key => (
                    <th key={key} style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row, idx) => (
                  <tr key={idx}>
                    {Object.values(row).map((val: any, vIdx) => (
                      <td key={vIdx}>{val !== null ? String(val) : '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="dashboard-grid">
          {reports.length === 0 ? (
            <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-secondary)' }}>Nenhum relatório personalizado cadastrado.</p>
              {isSA && <button className="btn" onClick={handleCreateNew}>Criar Primeiro Relatório</button>}
            </div>
          ) : (
            reports.map(report => (
              <div key={report.id} className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ marginBottom: '5px' }}>{report.name}</h3>
                    <span style={{ fontSize: '0.7rem', backgroundColor: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: '4px' }}>
                      {CATEGORIES.find(c => c.value === report.category)?.label}
                    </span>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '15px', height: '40px', overflow: 'hidden' }}>
                    {report.description || 'Sem descrição.'}
                  </p>
                  {report.is_row_report && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--accent-red)', border: '1px solid var(--accent-red)', padding: '1px 5px', borderRadius: '4px' }}>
                      Relatório de Linha
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                  <button className="btn" style={{ padding: '6px 12px' }} onClick={() => handleRun(report)}>Acessar</button>
                  {isSA && (
                    <ActionButtons
                      onEdit={() => handleEdit(report)}
                      onDelete={() => handleDelete(report.id)}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
